import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { adminWorkspaceApi, type AdminImpersonation, type ImpersonationStart } from './api';
import { authApi, type CurrentUser } from '../services/api';
import { AppUserContext } from './AppUserContext';
import {
  allowAdministratorRequests,
  clearSupportSession,
  installSupportSession,
  prepareSupportSession,
  hasSavedSupportSession,
  restoreSupportSession,
  SUPPORT_CHANGED,
} from './supportTransport';
import { SupportSessionContext } from './SupportSessionContext';
import './AdminWorkspace.css';

export default function SupportSessionProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId, sessionId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState<AdminImpersonation | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [busy, setBusy] = useState(hasSavedSupportSession);
  const [error, setError] = useState('');
  const [pendingDestination, setPendingDestination] = useState<string | null>(null);
  const authKey = `${userId || 'signed-out'}:${sessionId || ''}`;
  const [verifiedAuthKey, setVerifiedAuthKey] = useState<string | null>(null);
  // Ordinary visits can render while Clerk initializes. Saved support and later
  // identity changes still keep the subtree blocked until its binding is verified.
  const bindingChanged =
    (!isLoaded || verifiedAuthKey !== authKey) &&
    (verifiedAuthKey !== null || !!current || hasSavedSupportSession());
  const epoch = useRef(0);
  const currentRef = useRef(current);
  currentRef.current = current;
  useLayoutEffect(() => {
    // Re-enable normal administrator calls only after the target subtree has unmounted.
    if (!current && !busy && !error && !pendingDestination && !bindingChanged)
      allowAdministratorRequests();
  }, [current, busy, error, pendingDestination, bindingChanged]);
  useLayoutEffect(() => {
    if (pendingDestination && `${location.pathname}${location.search}` === pendingDestination) {
      setPendingDestination(null);
      setBusy(false);
    }
  }, [pendingDestination, location.pathname, location.search]);
  const resetCaches = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
  };
  const clear = async (destination: string | null = '/admin') => {
    setBusy(true);
    epoch.current += 1;
    currentRef.current = null;
    clearSupportSession();
    setCurrent(null);
    setUser(null);
    setError('');
    await resetCaches();
    if (destination) {
      setPendingDestination(destination);
      navigate(destination, { replace: true });
    } else setBusy(false);
  };
  const loadEffectiveUser = async (session: AdminImpersonation) => {
    const response = await authApi.getCurrentUser();
    if (!response.success || !response.data || response.data.clerkId !== session.target.clerkId)
      throw new Error(
        response.error || 'The support user could not be verified. Exit and try again.'
      );
    return response.data;
  };
  useEffect(() => {
    if (!isLoaded) return;
    const ownEpoch = ++epoch.current;
    let cancelled = false;
    const hadSavedSupport = hasSavedSupportSession();
    const wasActing = hadSavedSupport || !!currentRef.current;
    const firstNormalLoad = verifiedAuthKey === null && !wasActing;
    setVerifiedAuthKey(authKey);
    if (firstNormalLoad) {
      // Children may already be fetching public or signed-in data. There is no
      // previous identity to clear on an ordinary visit, so leave those queries intact.
      clearSupportSession();
      return () => {
        cancelled = true;
      };
    }
    setBusy(true);
    setCurrent(null);
    setUser(null);
    setError('');
    setPendingDestination(null);
    if (!isSignedIn || !userId || !sessionId) {
      if (wasActing) void clear('/sign-in?redirect_url=%2Fadmin');
      else {
        clearSupportSession();
        void resetCaches().then(() => {
          if (!cancelled) setBusy(false);
        });
      }
      return () => {
        cancelled = true;
      };
    }
    const restored = restoreSupportSession({ userId, sessionId });
    if (!restored) {
      if (hadSavedSupport) {
        void clear();
      } else
        void resetCaches().then(() => {
          if (!cancelled) setBusy(false);
        });
      return () => {
        cancelled = true;
      };
    }
    setCurrent(restored);
    (async () => {
      await resetCaches();
      const result = await adminWorkspaceApi.currentImpersonation();
      if (cancelled) return;
      if (!result.active || result.impersonation?.mode !== 'app') {
        await clear();
        return;
      }
      setCurrent(result.impersonation);
      const effective = await loadEffectiveUser(result.impersonation);
      if (!cancelled && ownEpoch === epoch.current) setUser(effective);
    })()
      .catch(() => {
        if (!cancelled)
          setError('Support details are unavailable. Exit to return to administration.');
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
    // Authentication changes invalidate the per-tab original-session binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, userId, sessionId]);
  useEffect(() => {
    if (!current) return;
    const expire = () => {
      if (Date.parse(current.expiresAt) <= Date.now()) void clear();
    };
    const timer = window.setTimeout(
      expire,
      Math.min(2147483647, Math.max(0, Date.parse(current.expiresAt) - Date.now()))
    );
    const changed = () => {
      if (
        currentRef.current &&
        userId &&
        sessionId &&
        !restoreSupportSession({ userId, sessionId })
      )
        void clear();
    };
    window.addEventListener(SUPPORT_CHANGED, changed);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(SUPPORT_CHANGED, changed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, userId, sessionId]);
  const open = async (result: ImpersonationStart) => {
    if (!userId || !sessionId || !isSignedIn) throw new Error('Sign in to administration first.');
    const ownEpoch = ++epoch.current;
    setBusy(true);
    setError('');
    try {
      prepareSupportSession(result, { userId, sessionId });
      const response = await adminWorkspaceApi.currentImpersonation();
      if (!response.active || response.impersonation?.mode !== 'app')
        throw new Error('The support session could not be verified. Try again.');
      installSupportSession(result, { userId, sessionId }, response.impersonation);
      await resetCaches();
      const effective = await loadEffectiveUser(response.impersonation);
      if (ownEpoch !== epoch.current)
        throw new Error('Your login changed while opening support. Return to administration.');
      installSupportSession(result, { userId, sessionId }, response.impersonation);
      setUser(effective);
      setCurrent(response.impersonation);
      setPendingDestination(result.redirectPath);
      navigate(result.redirectPath, { replace: true });
    } catch (err) {
      clearSupportSession();
      setCurrent(null);
      setUser(null);
      await resetCaches();
      try {
        await adminWorkspaceApi.abortImpersonation(result.id, { userId, sessionId });
      } catch {
        /* Server expiry still bounds access. */
      }
      setError(err instanceof Error ? err.message : 'The support session could not be opened.');
      throw err;
    } finally {
      setBusy(false);
    }
  };
  const exit = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await adminWorkspaceApi.endImpersonation();
      if (result.mode !== 'app' || !result.originalSessionPreserved)
        throw new Error('The support session could not be closed safely. Try again.');
      await clear();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to end support. Try again.');
      setBusy(false);
    }
  };
  const blocked =
    bindingChanged || busy || !!pendingDestination || (current && !user) || (!current && !!error);
  return (
    <SupportSessionContext.Provider value={{ open, current, error, busy, exit }}>
      <AppUserContext.Provider value={{ acting: !!current, user }}>
        {blocked ? (
          <main className="admin-session-switch adviser-workspace">
            <div>
              <ShieldCheck size={30} />
              <p className="adviser-eyebrow">Attributed support session</p>
              <h1>
                {error
                  ? 'Support details are unavailable'
                  : current
                    ? `Opening ${current.target.name}`
                    : 'Loading workspace'}
              </h1>
              <p role={error ? 'alert' : 'status'}>
                {error || 'Checking the account and its permissions…'}
              </p>
              {current ? (
                <button
                  type="button"
                  className="adviser-button"
                  disabled={busy}
                  onClick={() => void exit()}
                >
                  Exit support session
                </button>
              ) : (
                error && (
                  <button
                    type="button"
                    className="adviser-button"
                    onClick={() => {
                      setError('');
                      navigate('/admin', { replace: true });
                    }}
                  >
                    Return to administration
                  </button>
                )
              )}
            </div>
          </main>
        ) : (
          <div key={current?.id || 'admin'}>{children}</div>
        )}
      </AppUserContext.Provider>
    </SupportSessionContext.Provider>
  );
}
